/**
 * quality — one place that knows a phone is not a desktop.
 *
 * Decided once, at module load, from whether the pointer can hover. Not from the user agent,
 * and not re-evaluated: every consumer of this bakes it into a buffer size or a build, so a
 * value that changed underneath them would leave the scene half-built at two densities.
 *
 * WHAT ACTUALLY COSTS. The scene hands a phone about 1.2 million triangles — 607k of kelp and
 * 580k of manta ray — plus a shadow pass that draws the casters again. That is a desktop
 * workload on a GPU with a fraction of the fill rate, and the pixel count is not even the
 * problem: an iPhone at a 2x cap is asked for 1.6M pixels against a 1080p desktop's 2.07M, so
 * it is being handed nearly as many pixels with maybe a fifth of the hardware.
 *
 * So the three biggest levers, in the order they pay:
 *   - the backing store, which is pure fill and scales as the square of the cap
 *   - the kelp, whose count scales as the square of its spacing
 *   - the rays, which are instanced boxes and so scale with surface area
 *
 * Each is a number rather than a feature switch. Nothing is turned OFF on a phone: the same
 * reef, the same kelp that parts, the same day. It is the same game, drawn coarser.
 */

/** true for a phone or a tablet — a pointer that cannot hover */
export const TOUCH = typeof matchMedia === 'function'
  && matchMedia('(pointer: coarse)').matches;

/** `?lowfi` forces the mobile tier on a desktop, for looking at what a phone gets */
const FORCED = typeof location !== 'undefined'
  && new URLSearchParams(location.search).has('lowfi');

export const MOBILE = TOUCH || FORCED;

interface Quality {
  /** cap on devicePixelRatio */
  pixelCap: number;
  /** multiplier on the kelp's planting pitch */
  algaeSpacing: number;
  /** and on how much of it is planted */
  algaeWindow: number;
  /** the rays' voxel pitch, in model units */
  mantaVox: number;
  /** multiplier on the camera's stand-off distance */
  cameraZoom: number;
}

export const QUALITY: Quality = {
  /**
   * Cap on devicePixelRatio. 1.5 rather than 2 on a phone: at four inches across, the
   * difference between 1.5x and 2x is invisible and the fill cost is 44% lower.
   */
  pixelCap: MOBILE ? 1.5 : 2,

  /**
   * Multiplier on the kelp's planting pitch. Density goes as the inverse square, so 1.55 is
   * 42% of the stipes — still unmistakably a bed, and the parting still reads.
   */
  algaeSpacing: MOBILE ? 1.55 : 1,

  /**
   * How much of it is planted. LEFT ALONE, after an attempt to cut it to 0.78.
   *
   * Spacing and window look like the same knob and are not: spacing is cost, window is
   * COVERAGE. Shrinking the window saves the same triangles and it saves them by not planting
   * kelp where the camera is looking — a phone in landscape needs the bed out to 75 u each side
   * and the cut brought it in to 62, which is a straight edge across the frame where the sea
   * floor suddenly goes bald. Cost has to come out of the density instead, which is invisible.
   */
  algaeWindow: 1,

  /**
   * The rays' voxel pitch, in model units. Coarser on a phone because they are drawn as
   * instanced boxes rather than greedy-meshed, so they are the most expensive animal per pixel
   * of screen they occupy by a wide margin.
   */
  mantaVox: MOBILE ? 0.020 : 0.013,

  /**
   * Camera stand-off, as a multiplier. 20% closer on a phone.
   *
   * The rig's 26-34 u was framed for a monitor an arm's length away, where the shark is a
   * couple of inches of glass and the reef around it is legible. The same framing on a phone
   * held at the same distance makes the animal about a centimetre — too small to read the
   * markings anyone just spent a minute choosing, and too small to see which way it is pointing
   * while your thumb is asking it to go somewhere.
   *
   * It costs some of the reaction time the pull-back was buying, and that is the trade: on a
   * phone you can see the shark, and slightly less of what is ahead of it.
   */
  cameraZoom: MOBILE ? 0.8 : 1,
};
